import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  Header,
  Table,
  Checkbox,
  Dropdown,
  Button,
  Message,
  List,
  Loader,
  Dimmer,
  Form,
  Input,
  Icon,
  Label,
  Segment,
  Tab
} from 'semantic-ui-react';
import { simulationService } from '../../services/simulation/SimulationService';
import { planService } from '../../services/plans/PlanService';
import { getValidDdds, getValidDddsForFastchip } from '../../services/utils/dddUtils';
import { useToast } from '../../hooks/useToast';
import { useSimulationToContract } from '../../context/SimulationToContractContext';
import { useNavigate } from 'react-router-dom';
import { extractDddFromPhone } from '../../services/utils/stringUtils';
import { useAvailableICcIdsByState } from '../../hooks/useICcIds';
import {
  formatRjMgEsFastchipClusterUiLabel,
  iccidHasDhCluster,
  resolveRjMgEsFastchipIccidSearch,
} from '../../utils/fastchipClusterRules';
import {
  SimulationClientDto,
  SimulationLineDto,
  LinhaRenovacao,
  LinhaAditivo,
  SimulationRequestDto,
  SimulationResultDto,
  LinhaCalculadaDto,
  PlanOption
} from '../../types/simulation.types';
import { PlanResponse } from '../../types/dtos';
import SalesArgumentsPanel from './SalesArgumentsPanel';
import { useSessionUser } from '../../hooks/useSessionUser';
import { TeamContractRules, defaultContractRules } from '../../services/teams/teamService';
import { loadTeamContractRules } from '../../utils/teamContractRules';
import {
  filterAditivoEligiblePlans,
  formatContractPlanOptionLabel,
} from '../../utils/contractPlanCompatibility';
import { useSuspensionBlockStatusQuery } from '../../hooks/clientCancellationLines/useClientCancellationLines';
import { SuspensionBlockBanner } from '../clientCancellationLines/SuspensionBlockBanner';
import { deveExibirBannerPoliticaSuspensao } from '../../utils/clientCancellationLines/clientCancellationSuspensionPolicy';

interface Props {
  open: boolean;
  onClose: () => void;
  clienteId: string;
}

export const SimulationModal: React.FC<Props> = ({ open, onClose, clienteId }) => {
  const { showSuccess, showError } = useToast();
  const suspensionPolicyQuery = useSuspensionBlockStatusQuery({ enabled: open });
  const suspensionPolicy = suspensionPolicyQuery.data;
  const { openContractModalWithSimulation } = useSimulationToContract();
  const navigate = useNavigate();
  const { getUserId, isAuthenticated } = useSessionUser();
  const aditivosSectionRef = useRef<HTMLDivElement>(null);
  const resultadoSectionRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  const [cliente, setCliente] = useState<SimulationClientDto | null>(null);
  const [linhasRenovacao, setLinhasRenovacao] = useState<LinhaRenovacao[]>([]);
  const [linhasAditivo, setLinhasAditivo] = useState<LinhaAditivo[]>([]);
  const [linhasSemWhatsApp, setLinhasSemWhatsApp] = useState<SimulationLineDto[]>([]);
  const [linhasParaSuspender, setLinhasParaSuspender] = useState<Set<string>>(new Set()); // IDs das linhas marcadas para suspender
  const [credenciaisMeuTim, setCredenciaisMeuTim] = useState<{ login: string; senha: string }>({ login: '', senha: '' });
  const [clienteCompleto, setClienteCompleto] = useState<any>(null);
  
  // States para ICCID - usar hook para buscar ICCIDs reais do cluster
  const {
    iccids: availableICcIdsRaw,
    loading: iccidsLoading,
    error: iccidsError
  } = useAvailableICcIdsByState(clienteCompleto?.estado || '');

  // Filtrar ICCIDs: Fastchip RJ/MG/ES só exige tag DH quando a flag DH está activa.
  const availableICcIds = useMemo(() => {
    const filtered = availableICcIdsRaw.filter(ic => ic.disponivel === true);
    const rjMgEsSearch = resolveRjMgEsFastchipIccidSearch(clienteCompleto?.estado);

    if (rjMgEsSearch?.usesDhCluster) {
      const iccidsCluster = availableICcIdsRaw.filter(
        (ic) => ic.disponivel === true && iccidHasDhCluster(ic.estadosInfo)
      );
      return iccidsCluster.map((ic) => ic.ccId);
    }

    return filtered.map(ic => ic.ccId);
  }, [availableICcIdsRaw, clienteCompleto?.estado]);
  const [planosOptions, setPlanosOptions] = useState<PlanOption[]>([]); // Para Renegociação (RP)
  const [planosOptionsAditivo, setPlanosOptionsAditivo] = useState<PlanOption[]>([]); // Para Aditivo (não-RP)
  const [planosRP, setPlanosRP] = useState<PlanResponse[]>([]);
  const [planosAditivo, setPlanosAditivo] = useState<PlanResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [validandoWhatsApp, setValidandoWhatsApp] = useState(false);
  const [numerosEmValidacao, setNumerosEmValidacao] = useState<Set<string>>(new Set());
  const [calculando, setCalculando] = useState(false);
  const [resultadoSimulacao, setResultadoSimulacao] = useState<SimulationResultDto | null>(null);
  const [teamContractRules, setTeamContractRules] = useState<TeamContractRules>(defaultContractRules);
  const [teamRulesLoaded, setTeamRulesLoaded] = useState(false);

  useEffect(() => {
    if (!open || !isAuthenticated) return;

    const currentUserId = getUserId();
    if (!currentUserId) return;

    setTeamRulesLoaded(false);
    loadTeamContractRules()
      .then((rules) => {
        setTeamContractRules(rules);
        setTeamRulesLoaded(true);
      })
      .catch(() => {
        setTeamRulesLoaded(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAuthenticated]);

  useEffect(() => {
    if (open && clienteId) {
      loadClienteData();
      loadPlanos();
    }
  }, [open, clienteId]);

  // O hook useAvailableICcIdsByState já carrega automaticamente quando clienteCompleto?.estado muda
  // Não é necessário useEffect adicional

  // Preencher ICCID automaticamente quando ICCIDs são carregados e linha tem fastchip
  // Garantir que não haja ICCIDs duplicados
  useEffect(() => {
    if (availableICcIds.length > 0 && linhasAditivo.length > 0) {
      const iccidsUtilizados = new Set<string>();
      const linhasAtualizadas = linhasAditivo.map(la => {
        // Se tem fastchip mas não tem ICCID, preencher automaticamente com um ICCID não utilizado
        if (la.fastchip && !la.iccid) {
          const iccidDisponivel = availableICcIds.find(iccid => !iccidsUtilizados.has(iccid));
          if (iccidDisponivel) {
            iccidsUtilizados.add(iccidDisponivel);
            return {
              ...la,
              iccid: iccidDisponivel
            };
          }
        } else if (la.fastchip && la.iccid) {
          // Marcar ICCID como utilizado
          iccidsUtilizados.add(la.iccid);
        }
        return la;
      });
      
      // Só atualizar se houver mudanças
      const precisaAtualizar = linhasAtualizadas.some((la, idx) => 
        la.iccid !== linhasAditivo[idx].iccid
      );
      
      if (precisaAtualizar) {
        setLinhasAditivo(linhasAtualizadas);
      }
    }
  }, [availableICcIds, linhasAditivo.length]);

  // Garantir que novas linhas adicionadas herdem fastchip se já existe uma linha com fastchip
  useEffect(() => {
    const temFastchip = linhasAditivo.some(la => la.fastchip);
    if (temFastchip && availableICcIds.length > 0) {
      const linhasSemFastchip = linhasAditivo.filter(la => !la.fastchip);
      if (linhasSemFastchip.length > 0) {
        // Obter ICCIDs já utilizados
        const iccidsUtilizados = new Set(linhasAditivo
          .filter(la => la.fastchip && la.iccid)
          .map(la => la.iccid));
        
        const linhasAtualizadas = linhasAditivo.map(la => {
          if (!la.fastchip) {
            // Encontrar um ICCID disponível que não esteja sendo usado
            const iccidDisponivel = availableICcIds.find(iccid => !iccidsUtilizados.has(iccid));
            if (iccidDisponivel) {
              iccidsUtilizados.add(iccidDisponivel);
              return {
                ...la,
                fastchip: true,
                iccid: iccidDisponivel
              };
            }
            return {
              ...la,
              fastchip: true
            };
          }
          return la;
        });
        
        // Verificar se realmente precisa atualizar para evitar loop
        const precisaAtualizar = linhasAtualizadas.some((la, idx) => 
          la.fastchip !== linhasAditivo[idx].fastchip || la.iccid !== linhasAditivo[idx].iccid
        );
        
        if (precisaAtualizar) {
          setLinhasAditivo(linhasAtualizadas);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linhasAditivo.length, availableICcIds.length]);

  // Scroll automático para resultado quando simulação é calculada
  useEffect(() => {
    if (resultadoSimulacao && resultadoSectionRef.current) {
      // Usar um timeout para garantir que o DOM foi atualizado e o componente renderizado
      const timeoutId = setTimeout(() => {
        const modalContent = document.querySelector('.ui.modal.visible .scrolling.content') as HTMLElement;
        if (modalContent && resultadoSectionRef.current) {
          // Calcular posição relativa ao container do modal
          const modalContentRect = modalContent.getBoundingClientRect();
          const elementRect = resultadoSectionRef.current.getBoundingClientRect();
          
          // Calcular a posição do elemento em relação ao topo do container
          const relativeTop = elementRect.top - modalContentRect.top + modalContent.scrollTop;
          
          // Scroll para posicionar o elemento no topo do container (com offset)
          modalContent.scrollTo({
            top: relativeTop - 20, // Offset de 20px do topo
            behavior: 'smooth'
          });
        } else if (resultadoSectionRef.current) {
          // Fallback: tentar scrollIntoView diretamente
          resultadoSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 400); // Aumentar timeout para garantir renderização
      
      return () => clearTimeout(timeoutId);
    }
  }, [resultadoSimulacao]);

  // Função para calcular aditivos necessários baseado em linhas suspensas
  // Regra: 1 aditivo base já cobre as primeiras 2 suspensões
  // A partir da 3ª suspensão, cada 2 suspensões = 1 aditivo adicional
  const calcularAditivosNecessarios = (linhasSuspensas: number): number => {
    return Math.max(1, Math.ceil(linhasSuspensas / 2));
  };

  // Aplicar ofertas inteligentes automaticamente ao carregar linhas
  useEffect(() => {
    if (cliente && planosRP.length > 0 && linhasRenovacao.length > 0) {
      // Verificar se alguma linha não tem plano simulado e precisa de oferta
      const precisaAplicarOfertas = linhasRenovacao.some(lr => 
        lr.selecionada && !lr.planoSimulado
      );
      
      if (precisaAplicarOfertas) {
        const linhasComOfertas = linhasRenovacao.map(lr => {
          // Garantir que todas as linhas selecionadas tenham oferta recomendada
          // Se não tem plano simulado, calcular automaticamente
          const planoRecomendado = lr.selecionada 
            ? (lr.planoSimulado || sugerirPlanoInteligente(lr.linha))
            : lr.planoSimulado;
          
          return {
            ...lr,
            planoSimulado: planoRecomendado
          };
        });
        setLinhasRenovacao(linhasComOfertas);
      }

      // Adicionar linha aditiva automática se não houver nenhuma (sempre adiciona pelo menos 1)
      if (linhasAditivo.length === 0) {
        const linhaAditivaSugerida = sugerirLinhaAditiva();
        if (linhaAditivaSugerida) {
          setLinhasAditivo([linhaAditivaSugerida]);
        }
      }
    } else if (cliente && planosAditivo.length > 0 && linhasRenovacao.length === 0 && linhasAditivo.length === 0) {
      // Se não há linhas de renegociação mas há cliente e planos, adicionar linha aditiva automaticamente
      const linhaAditivaSugerida = sugerirLinhaAditiva();
      if (linhaAditivaSugerida) {
        setLinhasAditivo([linhaAditivaSugerida]);
      }
    }
  }, [cliente, planosRP, planosAditivo, linhasRenovacao]);

  // Ajustar automaticamente linhas aditivas baseado em linhas suspensas
  useEffect(() => {
    if (!teamRulesLoaded || !teamContractRules.requireAditivosForSuspendedLines) return;
    if (!cliente || planosAditivo.length === 0) return;

    const linhasSuspensas = linhasParaSuspender.size;
    const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
    const aditivosAtuais = linhasAditivo.length;

    if (aditivosNecessarios > aditivosAtuais) {
      // Adicionar aditivos necessários
      const aditivosParaAdicionar = aditivosNecessarios - aditivosAtuais;
      const novosAditivos: LinhaAditivo[] = [];
      
      for (let i = 0; i < aditivosParaAdicionar; i++) {
        const linhaAditivaSugerida = sugerirLinhaAditiva();
        if (linhaAditivaSugerida) {
          novosAditivos.push(linhaAditivaSugerida);
        } else {
          // Se não conseguir sugerir, criar linha vazia
          novosAditivos.push({
            ddd: '',
            numero: '',
            iccid: '',
            planoSelecionado: null,
            fastchip: false,
            seraUsadaPeloCliente: null
          });
        }
      }

      setLinhasAditivo([...linhasAditivo, ...novosAditivos]);
      // Mostrar toast de warning/error e fazer scroll para a seção de aditivos
      showError(`⚠️ ${aditivosParaAdicionar} ${aditivosParaAdicionar === 1 ? 'linha aditiva foi adicionada' : 'linhas aditivas foram adicionadas'} automaticamente. ${linhasSuspensas} ${linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} ${aditivosNecessarios} ${aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'}.`);
      // Scroll automático para a seção de aditivos após um pequeno delay
      setTimeout(() => {
        aditivosSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (aditivosNecessarios < aditivosAtuais) {
      // Remover aditivos extras (mas sempre manter pelo menos o necessário)
      const aditivosParaRemover = aditivosAtuais - aditivosNecessarios;
      const novasLinhasAditivo = [...linhasAditivo];
      novasLinhasAditivo.splice(0, aditivosParaRemover);
      setLinhasAditivo(novasLinhasAditivo);
    }
  }, [linhasParaSuspender.size, cliente, planosAditivo, teamRulesLoaded, teamContractRules.requireAditivosForSuspendedLines]);

  const loadClienteData = async () => {
    setLoading(true);
    try {
      const data = await simulationService.getClientDetails(clienteId);
      setCliente(data);
      
      // O estado já vem da API
      setClienteCompleto(data);
      
      // Inicializar linhas sem ofertas inteligentes (serão aplicadas depois pelo useEffect)
      // Filtrar linhas que já sabemos que não têm WhatsApp (se temWhatsApp === false)
      const linhasComWhatsApp = data.linhas.filter(linha => linha.temWhatsApp !== false);
      
      const linhasIniciais = linhasComWhatsApp.map(linha => ({
        linha,
        selecionada: true,
        planoSimulado: null // Iniciar como null para que o useEffect aplique a oferta inteligente
      }));
      
      setLinhasRenovacao(linhasIniciais);
      
      // Armazenar linhas sem WhatsApp inicialmente
      const linhasSemWhatsAppInicial = data.linhas.filter(linha => linha.temWhatsApp === false);
      setLinhasSemWhatsApp(linhasSemWhatsAppInicial);
      
      // Validar WhatsApp em background para confirmar/atualizar status
      validateWhatsAppInBackground(data.linhas.map(l => l.numeroLinha));
      
      // Aplicar ofertas inteligentes automaticamente após carregar planos
      // Isso será feito no useEffect que monitora planosRP
    } catch (error: any) {
      showError(error.message || 'Erro ao carregar dados do cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadPlanos = async () => {
    try {
      // Usar getAllPlans para obter todos os planos sem paginação
      const todosPlanos = await planService.getAllPlans();
      
      // Filtrar planos RP (para Renegociação) - planos que contêm "RP" no nome
      const planosRPList = todosPlanos.filter((plano: PlanResponse) => 
        plano.name.includes('RP')
      );
      setPlanosRP(planosRPList);
      
      const planosAditivoList = filterAditivoEligiblePlans(todosPlanos);
      setPlanosAditivo(planosAditivoList);
      
      // Criar opções para Renegociação (RP)
      const optionsRP: PlanOption[] = planosRPList.map((plano: PlanResponse) => ({
        key: plano.id,
        text: formatContractPlanOptionLabel(plano),
        value: plano.id
      }));
      setPlanosOptions(optionsRP);
      
      const optionsAditivo: PlanOption[] = planosAditivoList.map((plano: PlanResponse) => ({
        key: plano.id,
        text: formatContractPlanOptionLabel(plano),
        value: plano.id
      }));
      setPlanosOptionsAditivo(optionsAditivo);
      
    } catch (error) {
      showError('Erro ao carregar planos. Tente novamente.');
    }
  };

  const getPlanosDisponiveisParaLinha = (linha: SimulationLineDto): PlanOption[] => {
    // Se não há cliente ou planos RP, retornar vazio
    if (!cliente || !planosRP.length || !planosOptions.length) {
      return [];
    }
    
    // Extrair a franquia REAL da linha do cliente (ex: "20GB" -> 20, "30GB" -> 30)
    const franquiaAtualStr = linha.franquiaAtual || '0GB';
    const franquiaAtualGB = parseInt(franquiaAtualStr.replace(/[^0-9]/g, '')) || 0;
    const franquiaAtualMB = franquiaAtualGB * 1024;
    
    // Se fidelidade = 0, pode fazer downgrade (franquia menor ou igual à REAL da linha)
    // Se fidelidade > 0, apenas upgrade (franquia maior que a REAL da linha)
    const planosFiltrados = cliente.fidelidade === 0
      ? planosOptions.filter(opt => {
          const plano = planosRP.find(p => p.id === opt.value);
          return plano && plano.dataAllowanceMb <= franquiaAtualMB;
        })
      : planosOptions.filter(opt => {
          const plano = planosRP.find(p => p.id === opt.value);
          return plano && plano.dataAllowanceMb > franquiaAtualMB;
        });
    
    return planosFiltrados;
  };

  const validateWhatsAppInBackground = async (numeros: string[]) => {
    if (!numeros || numeros.length === 0) {
      return;
    }
    
    // Marcar números como em validação
    setNumerosEmValidacao(prev => {
      const novos = new Set(prev);
      numeros.forEach(num => novos.add(num));
      return novos;
    });
    
    setValidandoWhatsApp(true);
    try {
      const validacoes = await simulationService.validateWhatsApp(numeros);
      
      // Normalizar números (remover formatação e código do país) para comparação
      const normalizarNumero = (num: string) => {
        const limpo = num.replace(/\D/g, '');
        // Remover código do país 55 se presente
        return limpo.startsWith('55') ? limpo.substring(2) : limpo;
      };
      // Filtrar linhas sem WhatsApp
      const semWhatsApp = linhasRenovacao
        .map(lr => lr.linha)
        .filter(linha => {
          const numeroNormalizado = normalizarNumero(linha.numeroLinha);
          
          // Verificar se o número está nas validações e NÃO tem WhatsApp (validacoes[key] === false)
          let encontrado = false;
          for (const key of Object.keys(validacoes)) {
            const keyNormalizado = normalizarNumero(key);
            if (keyNormalizado === numeroNormalizado) {
              if (validacoes[key] === false) {
                encontrado = true;
                break;
              }
            }
          }
          return encontrado;
        });
      
      // Combinar com linhas sem WhatsApp que já existiam (se houver)
      setLinhasSemWhatsApp(prev => {
        const todas = [...prev, ...semWhatsApp];
        // Remover duplicatas baseado no ID
        const unicas = todas.filter((linha, index, self) => 
          index === self.findIndex(l => l.id === linha.id)
        );
        return unicas;
      });
      
      // Atualizar status de WhatsApp nas linhas de renegociação e remover linhas sem WhatsApp
      setLinhasRenovacao(prevLinhas => {
        const linhasAtualizadas = prevLinhas.map(lr => {
          const numeroNormalizado = normalizarNumero(lr.linha.numeroLinha);
          // Verificar se o número está nas validações
          let temWhatsAppStatus: boolean | null = null;
          for (const key of Object.keys(validacoes)) {
            const keyNormalizado = normalizarNumero(key);
            
            if (keyNormalizado === numeroNormalizado) {
              temWhatsAppStatus = validacoes[key] === true;
              break;
            }
          }
          
          // Se encontrou validação, atualizar o status
          if (temWhatsAppStatus !== null) {
            return {
              ...lr,
              linha: {
                ...lr.linha,
                temWhatsApp: temWhatsAppStatus
              }
            };
          }
          
          return lr;
        });
        
        // Filtrar linhas sem WhatsApp (remover da lista de renegociação)
        const linhasFiltradas = linhasAtualizadas.filter(lr => {
          const numeroNormalizado = normalizarNumero(lr.linha.numeroLinha);
          // Verificar se o número tem WhatsApp (true) ou não foi validado ainda
          const temWhatsApp = Object.keys(validacoes).some(key => {
            const keyNormalizado = normalizarNumero(key);
            return keyNormalizado === numeroNormalizado && validacoes[key] === true;
          });
          const naoValidado = !Object.keys(validacoes).some(key => {
            const keyNormalizado = normalizarNumero(key);
            return keyNormalizado === numeroNormalizado;
          });
          // Manter se temWhatsApp === true ou se não foi validado ainda
          return temWhatsApp || naoValidado;
        });
        
        // Se todas as linhas foram removidas (todas sem WhatsApp validado), manter todas
        // Isso garante que a seção de renegociação apareça mesmo quando todas as linhas não têm WhatsApp
        // O usuário ainda pode trabalhar com essas linhas (suspender, etc)
        if (linhasFiltradas.length === 0 && linhasAtualizadas.length > 0) {
          return linhasAtualizadas; // Manter todas as linhas mesmo sem WhatsApp
        }
        
        return linhasFiltradas;
      });
      
      // Remover números da lista de validação
      setNumerosEmValidacao(prev => {
        const novos = new Set(prev);
        numeros.forEach(num => novos.delete(num));
        return novos;
      });
    } catch (error) {
      // Silencioso - não quebra a tela
      // Remover números da lista de validação mesmo em caso de erro
      setNumerosEmValidacao(prev => {
        const novos = new Set(prev);
        numeros.forEach(num => novos.delete(num));
        return novos;
      });
    } finally {
      setValidandoWhatsApp(false);
    }
  };

  const adicionarNovaLinha = () => {
    // Verificar se já existe alguma linha com fastchip
    const temFastchip = linhasAditivo.some(la => la.fastchip);
    
    // Obter ICCIDs já utilizados
    const iccidsUtilizados = linhasAditivo
      .filter(la => la.fastchip && la.iccid)
      .map(la => la.iccid);
    
    // Encontrar um ICCID disponível que não esteja sendo usado
    const iccidDisponivel = availableICcIds.find(iccid => !iccidsUtilizados.includes(iccid));
    
    const novaLinha: LinhaAditivo = {
      ddd: '', 
      numero: '', 
      iccid: temFastchip && iccidDisponivel ? iccidDisponivel : '', 
      planoSelecionado: null, 
      fastchip: temFastchip, // Se já existe fastchip, nova linha também será fastchip
      seraUsadaPeloCliente: null
    };
    
    setLinhasAditivo([...linhasAditivo, novaLinha]);
  };

  const removerLinhaAditivo = (index: number) => {
    setLinhasAditivo(linhasAditivo.filter((_, i) => i !== index));
  };

  const atualizarLinhaRenovacao = (index: number, campo: keyof LinhaRenovacao, valor: any) => {
    const novasLinhas = [...linhasRenovacao];
    novasLinhas[index] = { ...novasLinhas[index], [campo]: valor === null ? null : valor };
    setLinhasRenovacao(novasLinhas);
  };

  const atualizarLinhaAditivo = (index: number, campo: keyof LinhaAditivo, valor: any) => {
    const novasLinhas = [...linhasAditivo];
    novasLinhas[index] = { ...novasLinhas[index], [campo]: valor === null ? null : valor };
    setLinhasAditivo(novasLinhas);
  };

  // Função para adicionar linha manual com validação de WhatsApp
  const adicionarLinhaManual = async () => {
    const numero = (document.getElementById('manual-line-number') as HTMLInputElement)?.value;
    const franquia = (document.getElementById('manual-line-data') as HTMLInputElement)?.value;
    const planDropdown = document.getElementById('manual-line-plan') as any;
    const planId = planDropdown?.querySelector('.text')?.textContent;
    
    if (!numero || !franquia) {
      showError('Preencha número e franquia da linha');
      return;
    }
    
    // Validar formato do número
    const numeroLimpo = numero.replace(/\D/g, '');
    if (numeroLimpo.length < 10 || numeroLimpo.length > 11) {
      showError('Número deve ter 10 ou 11 dígitos');
      return;
    }
    
    // Encontrar plano pelo ID se foi selecionado
    const planoSelecionado = planId ? (planosRP.find(p => p.name === planId)?.id ?? null) : null;
    
    // Criar nova linha manual com status de WhatsApp pendente
    const novaLinha: LinhaRenovacao = {
      linha: {
        id: `manual-${Date.now()}`,
        numeroLinha: numeroLimpo,
        planoAtual: franquia,
        franquiaAtual: franquia,
        planId: planoSelecionado,
        temWhatsApp: null, // Inicialmente null = não validado
        dataValidacaoWhatsApp: null,
        statusLinha: 'Manual'
      },
      planoSimulado: null,
      selecionada: true
    };
    
    // Adicionar ao estado
    setLinhasRenovacao([...linhasRenovacao, novaLinha]);
    showSuccess(`Linha ${numeroLimpo} adicionada. Validando WhatsApp...`);
    
    // Limpar campos
    (document.getElementById('manual-line-number') as HTMLInputElement).value = '';
    (document.getElementById('manual-line-data') as HTMLInputElement).value = '';
    const dropdown = document.getElementById('manual-line-plan') as any;
    if (dropdown) {
      const clearIcon = dropdown.querySelector('.clear');
      if (clearIcon) clearIcon.click();
    }
    
    // Validar WhatsApp em background para o número inserido
    validateWhatsAppInBackground([numeroLimpo]);
  };

  // Função de sugestão inteligente de planos baseada na franquia real da linha
  const sugerirPlanoInteligente = (linha: SimulationLineDto, planoAtualSelecionado?: string | null): string | null => {
    if (!cliente || !planosRP.length) return null;
    
    // Extrair GB da franquiaAtual REAL da linha (ex: "25GB" -> 25, "20GB" -> 20)
    const franquiaAtualStr = linha.franquiaAtual || '0GB';
    const franquiaAtualGB = parseInt(franquiaAtualStr.replace(/[^0-9]/g, '')) || 0;
    const franquiaAtualMB = franquiaAtualGB * 1024;
    
    // Ordenar todos os planos por franquia (ascendente)
    const planosOrdenados = [...planosRP].sort((a, b) => a.dataAllowanceMb - b.dataAllowanceMb);
    
    let planoSugerido: any = null;
    
    if (cliente.fidelidade === 0) {
      // Sem fidelidade: sugerir o PRIMEIRO plano MAIOR que a franquia REAL da linha
      // Ex: linha 20GB → primeiro plano > 20GB (25GB, se existir)
      planoSugerido = planosOrdenados.find(p => p.dataAllowanceMb > franquiaAtualMB);
      
      // Se não há plano maior, manter no maior disponível
      if (!planoSugerido) {
        planoSugerido = planosOrdenados[planosOrdenados.length - 1];
      }
    } else {
      // Com fidelidade: APENAS upgrade - buscar primeiro plano MAIOR que a franquia REAL
      // Ex: linha 20GB → primeiro plano > 20GB (25GB, se existir)
      // Ex: linha 50GB → primeiro plano > 50GB (60GB, se existir)
      planoSugerido = planosOrdenados.find(p => p.dataAllowanceMb > franquiaAtualMB);
      
      // Se não há plano maior, manter no maior disponível
      if (!planoSugerido) {
        planoSugerido = planosOrdenados[planosOrdenados.length - 1];
      }
    }
    
    // Se não encontrou plano sugerido, tentar usar o planId da linha
    return planoSugerido?.id || linha.planId || null;
  };

  // Função para sugerir linha aditiva automática baseada no ticket médio
  const sugerirLinhaAditiva = (): LinhaAditivo | null => {
    if (!cliente || !planosAditivo.length) return null;
    
    // Selecionar o plano de maior valor para dar margem de negociação
    // Ordenar por totalMonthlyPrice em ordem decrescente e pegar o primeiro
    const planoSugerido = planosAditivo
      .sort((a, b) => b.totalMonthlyPrice - a.totalMonthlyPrice)[0];
    
    if (!planoSugerido) return null;
    
    return {
      ddd: '', // Operador precisa preencher
      numero: '',
      iccid: '',
      planoSelecionado: planoSugerido.id,
      fastchip: false,
      seraUsadaPeloCliente: null
    };
  };

  // Função para sugerir plano aleatório respeitando regras de fidelidade
  const sugerirPlanoAleatorio = (linha: SimulationLineDto, planoAtualSelecionado?: string | null): string | null => {
    if (!cliente || !planosRP.length) return null;
    
    // Buscar plano atual
    let planoAtual: any = undefined;
    if (planoAtualSelecionado) {
      planoAtual = planosRP.find(p => p.id === planoAtualSelecionado);
    }
    
    if (!planoAtual) {
      const franquiaAtualStr = linha.franquiaAtual || '0GB';
      const franquiaAtualGB = parseInt(franquiaAtualStr.replace(/[^0-9]/g, '')) || 0;
      const franquiaAtualMB = franquiaAtualGB * 1024;
      const planosOrdenados = [...planosRP].sort((a, b) => a.dataAllowanceMb - b.dataAllowanceMb);
      planoAtual = planosOrdenados.find(p => p.dataAllowanceMb >= franquiaAtualMB) || planosOrdenados[planosOrdenados.length - 1];
    }
    
    if (!planoAtual) return linha.planId || null;
    
    const franquiaAtual = planoAtual.dataAllowanceMb;
    const planosOrdenados = [...planosRP].sort((a, b) => a.dataAllowanceMb - b.dataAllowanceMb);
    
    // Filtrar planos disponíveis respeitando regras de fidelidade
    let planosDisponiveis: any[] = [];
    
    if (cliente.fidelidade === 0) {
      // Sem fidelidade: pode fazer downgrade OU upgrade (qualquer plano)
      planosDisponiveis = planosOrdenados;
    } else {
      // Com fidelidade: APENAS upgrade (planos maiores que o atual)
      planosDisponiveis = planosOrdenados.filter(p => p.dataAllowanceMb > franquiaAtual);
    }
    
    // Se não há planos disponíveis, manter o atual
    if (planosDisponiveis.length === 0) {
      return planoAtual.id;
    }
    
    // Remover o plano atual da lista para garantir que seja diferente
    const planosSemAtual = planosDisponiveis.filter(p => p.id !== planoAtual.id);
    
    // Se não há outros planos além do atual, manter o atual
    if (planosSemAtual.length === 0) {
      return planoAtual.id;
    }
    
    // Selecionar um plano aleatório da lista
    const indiceAleatorio = Math.floor(Math.random() * planosSemAtual.length);
    return planosSemAtual[indiceAleatorio].id;
  };

  // Função para recalcular todas as ofertas de renegociação (apenas linhas não suspensas)
  const recalcularTodasOfertas = () => {
    if (!cliente || !planosRP.length) {
      showError('Planos ainda não foram carregados. Aguarde um momento e tente novamente.');
      return;
    }
    
    // Recalcular TODAS as linhas que NÃO estão marcadas para suspender
    const novasLinhas = linhasRenovacao.map(lr => {
      // Se a linha está marcada para suspender, não recalcular - manter como está
      if (linhasParaSuspender.has(lr.linha.id)) {
        return lr;
      }
      // Recalcular TODAS as linhas não suspensas com plano aleatório (independente de estarem selecionadas ou não)
      const novoPlano = sugerirPlanoAleatorio(lr.linha, lr.planoSimulado);
      return {
        ...lr,
        planoSimulado: novoPlano
      };
    });
    setLinhasRenovacao(novasLinhas);
    showSuccess('Ofertas recalculadas aleatoriamente para todas as linhas de renegociação (exceto suspensas)');
  };

  // Função para recalcular oferta de uma linha específica
  const recalcularOfertaLinha = (index: number) => {
    const linha = linhasRenovacao[index];
    if (!linha || linha.selecionada === false) return;
    
    // Não recalcular se a linha está marcada para suspender
    if (linhasParaSuspender.has(linha.linha.id)) return;
    
    // Usar sugestão aleatória para recalcular
    const planoIdSugerido = sugerirPlanoAleatorio(linha.linha, linha.planoSimulado);
    atualizarLinhaRenovacao(index, 'planoSimulado', planoIdSugerido);
    showSuccess(`Oferta recalculada aleatoriamente para linha ${linha.linha.numeroLinha}`);
  };

  // Função para recalcular todas as ofertas de linhas aditivas
  const recalcularTodasOfertasAditivo = () => {
    if (!cliente || planosAditivo.length === 0) {
      showError('Planos ainda não foram carregados. Aguarde um momento e tente novamente.');
      return;
    }
    
    // Recalcular planos para TODAS as linhas aditivas com seleção aleatória
    const novasLinhas = linhasAditivo.map(la => {
      // Selecionar um plano aleatório da lista de planos aditivos
      // Se já tem um plano selecionado, escolher outro aleatório
      const planosDisponiveis = la.planoSelecionado
        ? planosAditivo.filter(p => p.id !== la.planoSelecionado)
        : planosAditivo;
      
      if (planosDisponiveis.length > 0) {
        const indiceAleatorio = Math.floor(Math.random() * planosDisponiveis.length);
        return {
          ...la,
          planoSelecionado: planosDisponiveis[indiceAleatorio].id
        };
      } else if (planosAditivo.length > 0) {
        // Se não há outros planos disponíveis, escolher qualquer um aleatoriamente
        const indiceAleatorio = Math.floor(Math.random() * planosAditivo.length);
        return {
          ...la,
          planoSelecionado: planosAditivo[indiceAleatorio].id
        };
      }
      return la;
    });
    setLinhasAditivo(novasLinhas);
    showSuccess('Planos recalculados aleatoriamente para todas as linhas aditivas');
  };

  // Função loadIccidsForState removida - agora usando hook useAvailableICcIdsByState
  // O hook busca automaticamente os ICCIDs do cluster baseado no estado do cliente

  // Função para gerar detalhes de simulação localmente
  const gerarDetalhesSimulacao = (): LinhaCalculadaDto[] => {
    const detalhes: LinhaCalculadaDto[] = [];
    
    // Processar renovações (INCLUINDO linhas suspensas para mostrar na tabela)
    linhasRenovacao.forEach(lr => {
      if (lr.selecionada && lr.planoSimulado) {
        const planoNovo = planosRP.find(p => p.id === lr.planoSimulado);
        const planoAtual = planosRP.find(p => p.id === lr.linha.planId);
        
        // Verificar se a linha está marcada para suspender
        const estaSuspensa = linhasParaSuspender.has(lr.linha.id);
        
        if (planoNovo) {
          detalhes.push({
            numeroLinha: lr.linha.numeroLinha,
            planoAnterior: lr.linha.planoAtual,
            planoNovo: planoNovo.name,
            valorAnterior: planoAtual?.totalMonthlyPrice || 0,
            valorNovo: planoNovo.totalMonthlyPrice,
            diferenca: planoNovo.totalMonthlyPrice - (planoAtual?.totalMonthlyPrice || 0),
            tipoOperacao: 'Renegociação',
            seraSuspensa: estaSuspensa // Marcar se será suspensa
          });
        }
      }
    });
    
    // Processar aditivos
    linhasAditivo.forEach(la => {
      if (la.ddd && la.planoSelecionado) {
        const plano = planosAditivo.find(p => p.id === la.planoSelecionado);
        if (plano) {
          detalhes.push({
            numeroLinha: `${la.ddd} (Nova)`,
            planoAnterior: 'Nova linha',
            planoNovo: plano.name,
            valorAnterior: 0,
            valorNovo: plano.totalMonthlyPrice,
            diferenca: plano.totalMonthlyPrice,
            tipoOperacao: 'Aditivo',
            seraSuspensa: false // Aditivos nunca são suspensos
          });
        }
      }
    });
    
    return detalhes;
  };

  const handleCalculate = async () => {
    if (!cliente) return;

    setCalculando(true);
    try {
      // Verificar se há linhas selecionadas
      const linhasRenovacaoSelecionadas = linhasRenovacao.filter(lr => lr.selecionada && lr.planoSimulado);
      const linhasAditivoValidas = linhasAditivo.filter(la => 
        la.ddd && la.planoSelecionado && (!la.fastchip || la.iccid)
      );

      if (linhasRenovacaoSelecionadas.length === 0 && linhasAditivoValidas.length === 0) {
        showError('Selecione pelo menos uma linha para simular');
        return;
      }

      // Validar que todas as linhas aditivas têm DDD preenchido
      const linhasAditivoSemDdd = linhasAditivo.filter(la => 
        la.planoSelecionado && !la.ddd
      );
      if (linhasAditivoSemDdd.length > 0) {
        showError(`Por favor, selecione o DDD para ${linhasAditivoSemDdd.length === 1 ? 'a linha aditiva' : 'todas as linhas aditivas'}. O DDD é obrigatório.`);
        return;
      }

      // Validar que todas as linhas aditivas têm informação sobre uso pelo cliente
      const linhasAditivoSemUso = linhasAditivo.filter(la => 
        la.planoSelecionado && (la.seraUsadaPeloCliente === null || la.seraUsadaPeloCliente === undefined)
      );
      if (linhasAditivoSemUso.length > 0) {
        showError(`Por favor, informe se o cliente usará ${linhasAditivoSemUso.length === 1 ? 'a linha aditiva' : 'as linhas aditivas'}. Esta informação é obrigatória.`);
        return;
      }

      // Gerar detalhes de simulação localmente
      const detalhesLinhas = gerarDetalhesSimulacao();
      
      // Calcular totais (EXCLUINDO linhas suspensas)
      const valorTotalRenovacao = detalhesLinhas
        .filter(d => d.tipoOperacao === 'Renegociação' && !d.seraSuspensa)
        .reduce((total, d) => total + d.valorNovo, 0);
      
      const valorTotalAditivo = detalhesLinhas
        .filter(d => d.tipoOperacao === 'Aditivo')
        .reduce((total, d) => total + d.valorNovo, 0);
      
      const valorTotal = valorTotalRenovacao + valorTotalAditivo;
      
      // Criar resultado da simulação
      const resultado: SimulationResultDto = {
        valorTotalRenovacao,
        valorTotalAditivo,
        valorTotal,
        totalLinhasRenovacao: detalhesLinhas.filter(d => d.tipoOperacao === 'Renegociação' && !d.seraSuspensa).length,
        totalLinhasAditivo: linhasAditivoValidas.length,
        detalhesLinhas
      };
      
      setResultadoSimulacao(resultado);
      showSuccess('Simulação calculada com sucesso!');
      
      // Verificar se pode executar contrato rápido automaticamente
      if (canShowFastContractButton()) {
        const linhasSuspensas = linhasParaSuspender.size;
        const aditivosOk =
          !teamContractRules.requireAditivosForSuspendedLines ||
          linhasSuspensas === 0 ||
          linhasAditivo.filter(la =>
            la.ddd && la.planoSelecionado && (!la.fastchip || la.iccid)
          ).length >= calcularAditivosNecessarios(linhasSuspensas);

        if (aditivosOk) {
          setTimeout(() => {
            handleFastContract();
          }, 500);
        }
      }
    } catch (error: any) {
      showError(error.message || 'Erro ao calcular simulação');
    } finally {
      setCalculando(false);
    }
  };

  const handleClose = () => {
    setCliente(null);
    setLinhasRenovacao([]);
    setLinhasAditivo([]);
    setLinhasSemWhatsApp([]);
    setResultadoSimulacao(null);
    onClose();
  };

  const calcularValorSimulado = (): number => {
    let total = 0;
    
    // Somar valores das linhas de renegociação selecionadas (excluindo linhas suspensas)
    linhasRenovacao
      .filter(lr => !linhasParaSuspender.has(lr.linha.id))
      .forEach(lr => {
        if (lr.selecionada && lr.planoSimulado) {
          const plano = planosRP.find(p => p.id === lr.planoSimulado);
          if (plano) {
            total += plano.totalMonthlyPrice;
          }
        }
      });
    
    // Somar valores das linhas aditivas (buscar em planosAditivo)
    linhasAditivo.forEach(la => {
      if (la.planoSelecionado) {
        const plano = planosAditivo.find(p => p.id === la.planoSelecionado);
        if (plano) {
          total += plano.totalMonthlyPrice;
        }
      }
    });
    
    return total;
  };

  const handleFastContract = () => {
    if (!cliente) {
      showError('Cliente não carregado');
      return;
    }

    // Preparar linhas para suspender (usando numeroLinha como identificador, SEM duplicatas)
    // Usar Set para garantir unicidade
    const linhasParaSuspenderSet = new Set<string>();
    
    linhasRenovacao
      .filter(lr => linhasParaSuspender.has(lr.linha.id))
      .forEach(lr => linhasParaSuspenderSet.add(lr.linha.numeroLinha));
    
    linhasSemWhatsApp
      .filter(linha => linhasParaSuspender.has(linha.id))
      .forEach(linha => linhasParaSuspenderSet.add(linha.numeroLinha));
    
    const linhasParaSuspenderNumeros = Array.from(linhasParaSuspenderSet);

    // Validar que todas as linhas aditivas têm DDD preenchido
    const linhasAditivoSemDdd = linhasAditivo.filter(la => 
      la.planoSelecionado && !la.ddd
    );
    if (linhasAditivoSemDdd.length > 0) {
      showError(`Por favor, selecione o DDD para ${linhasAditivoSemDdd.length === 1 ? 'a linha aditiva' : 'todas as linhas aditivas'}. O DDD é obrigatório para criar o contrato.`);
      return;
    }

    // Validar que todas as linhas aditivas têm informação sobre uso pelo cliente
    const linhasAditivoSemUso = linhasAditivo.filter(la => 
      la.planoSelecionado && (la.seraUsadaPeloCliente === null || la.seraUsadaPeloCliente === undefined)
    );
    if (linhasAditivoSemUso.length > 0) {
      showError(`Por favor, informe se o cliente usará ${linhasAditivoSemUso.length === 1 ? 'a linha aditiva' : 'as linhas aditivas'}. Esta informação é obrigatória para criar o contrato.`);
      return;
    }

    // Validar regra de aditivos: verificar se há aditivos suficientes
    const linhasSuspensas = linhasParaSuspender.size;
    if (teamContractRules.requireAditivosForSuspendedLines && linhasSuspensas > 0) {
      const aditivosNecessarios = calcularAditivosNecessarios(linhasSuspensas);
      const aditivosValidosCount = linhasAditivo.filter(la =>
        la.ddd && la.planoSelecionado && (!la.fastchip || la.iccid) && (la.seraUsadaPeloCliente !== null && la.seraUsadaPeloCliente !== undefined)
      ).length;

      if (aditivosValidosCount < aditivosNecessarios) {
        showError(
          `Regra de aditivos não atendida: ${linhasSuspensas} ${linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} ${aditivosNecessarios} ${aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'}, mas apenas ${aditivosValidosCount} ${aditivosValidosCount === 1 ? 'foi encontrado' : 'foram encontrados'}. Por favor, adicione mais linhas aditivas.`
        );
        return;
      }
    }

    // Preparar linhas de renegociação válidas (selecionadas e com plano, INCLUINDO suspensas)
    const linhasRenovacaoValidas = linhasRenovacao
      .filter(lr => lr.selecionada && lr.planoSimulado)
      .map(lr => {
        const estaSuspensa = linhasParaSuspender.has(lr.linha.id);
        return {
          numeroLinha: lr.linha.numeroLinha,
          planId: lr.planoSimulado!,
          estaSuspensa // Adicionar flag para identificar linhas suspensas
        };
      });

    // Preparar linhas aditivas válidas
    const linhasAditivoValidas = linhasAditivo
      .filter(la => la.ddd && la.planoSelecionado && (!la.fastchip || la.iccid) && (la.seraUsadaPeloCliente !== null && la.seraUsadaPeloCliente !== undefined))
      .map(la => ({
        ddd: la.ddd,
        numero: la.numero, // Número completo (já inclui os 9 dígitos)
        planId: la.planoSelecionado!,
        iccid: la.iccid,
        fastchip: la.fastchip,
        seraUsadaPeloCliente: la.seraUsadaPeloCliente!
      }));

    // Validar que há pelo menos uma linha não suspensa
    const linhasNaoSuspensas = linhasRenovacaoValidas.filter(lr => !lr.estaSuspensa).length;
    if (linhasNaoSuspensas === 0 && linhasAditivoValidas.length === 0) {
      showError('Selecione pelo menos uma linha para criar o contrato (não pode ter apenas linhas suspensas)');
      return;
    }

    // Preparar dados para o contexto
    const simulationData = {
      clienteId: cliente.id,
      clienteCnpj: cliente.cnpj,
      linhasRenovacao: linhasRenovacaoValidas,
      linhasAditivo: linhasAditivoValidas,
      linhasParaSuspender: linhasParaSuspenderNumeros, // Usar numeroLinha como identificador
      credenciaisMeuTim: credenciaisMeuTim.login || credenciaisMeuTim.senha ? credenciaisMeuTim : undefined
    };

    // Enviar para o contexto
    openContractModalWithSimulation(simulationData);

    // Fechar modal de simulação
    handleClose();

    // Navegar para página de contratos
    navigate('/contracts');

    // Mensagem de sucesso
    showSuccess('Dados da simulação foram transferidos para criação de contrato!');
  };
  
  // Verificar se pode exibir o botão "Contrato Rápido"
  // O botão deve aparecer apenas após calcular simulação com sucesso (resposta 3b)
  const canShowFastContractButton = (): boolean => {
    if (!cliente) return false;
    
    // Botão deve aparecer apenas se a simulação foi calculada
    if (resultadoSimulacao === null) return false;
    
    // Verificar se há linhas de renegociação selecionadas OU linhas aditivas válidas
    const hasRenovacao = linhasRenovacao.some(lr => lr.selecionada && lr.planoSimulado);
    const hasAditivo = linhasAditivo.some(la => la.ddd && la.planoSelecionado && (!la.fastchip || la.iccid));
    
    return hasRenovacao || hasAditivo;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="large"
      closeIcon
      style={{
        maxWidth: '90vw',
      }}
    >
      <Modal.Header style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '20px 24px',
        borderBottom: 'none'
      }}>
        <Header as="h2" style={{ color: 'white', margin: 0 }}>
          <Icon name="calculator" style={{ color: 'white' }} />
          Simulação de Planos - {cliente?.razaoSocial || 'Carregando...'}
        </Header>
      </Modal.Header>
      
      <Modal.Content scrolling style={{ padding: '24px', backgroundColor: '#f8f9fa' }}>
        <Tab
          panes={[
            {
              menuItem: { 
                key: 'simulator', 
                icon: 'calculator', 
                content: 'Simulador' 
              },
              render: () => (
                <Tab.Pane style={{ padding: 0 }}>
                  <Dimmer active={loading} inverted>
                    <Loader content="Carregando dados do cliente..." />
                  </Dimmer>

        {validandoWhatsApp && (
          <Message info style={{ 
            backgroundColor: '#e3f2fd',
            borderRadius: '8px',
            borderLeft: '4px solid #2196f3'
          }}>
            <Message.Header>Validando WhatsApp</Message.Header>
            <p>Verificando números via Meta API...</p>
          </Message>
        )}

          {/* Informações do Cliente */}
          {cliente && (
            <Segment style={{ 
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <Header as="h3" style={{ color: '#2c3e50', marginTop: 0 }}>
                <Icon name="user" />
                Informações do Cliente
              </Header>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <div>
                  <strong>CNPJ:</strong> {cliente.cnpj}
                </div>
                <div>
                  <strong>Razão Social:</strong> {cliente.razaoSocial}
                </div>
                <div>
                  <strong>Admin:</strong> {cliente.nomeAdm}
                </div>
                <div>
                  <strong>Fidelidade:</strong> {cliente.fidelidade} meses
                  {cliente.fidelidade === 0 && (
                    <Label color="green" size="small" style={{ marginLeft: '8px' }}>
                      Pode fazer downgrade
                    </Label>
                  )}
                  {cliente.fidelidade > 0 && (
                    <Label color="orange" size="small" style={{ marginLeft: '8px' }}>
                      Apenas upgrade
                    </Label>
                  )}
                </div>
              </div>
            </Segment>
          )}

          {/* Seção: Novas Linhas (Aditivo) - Mostrar primeiro se não houver linhas de renegociação */}
          {linhasRenovacao.length === 0 && linhasAditivo.length > 0 && (
            <Segment 
              ref={aditivosSectionRef}
              style={{ 
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #ff9800'
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Header as="h3" style={{ color: '#e65100', margin: 0 }}>
                  <Icon name="plus circle" size="large" />
                  Novas Linhas (Aditivo)
                </Header>
                <Button 
                  icon="plus" 
                  content="Adicionar Nova Linha" 
                  onClick={adicionarNovaLinha}
                  positive
                  style={{ 
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontWeight: 500
                  }}
                />
              </div>
              
              <Table celled style={{ backgroundColor: '#ffffff' }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>DDD</Table.HeaderCell>
                    <Table.HeaderCell>Fastchip</Table.HeaderCell>
                    <Table.HeaderCell>ICCID</Table.HeaderCell>
                    <Table.HeaderCell>Plano</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{ textAlign: 'center' }}>Cliente Usará</Table.HeaderCell>
                    <Table.HeaderCell width={1}>Ações</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {linhasAditivo.map((linha, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell>
                        <Dropdown
                          placeholder="Selecione DDD *"
                          selection
                          search
                          value={linha.ddd}
                          options={
                            clienteCompleto?.estado
                              ? (() => {
                                  const ddds = getValidDddsForFastchip(
                                    clienteCompleto.estado, 
                                    linha.fastchip, 
                                    availableICcIds.length > 0
                                  );
                                  return ddds.map(ddd => ({
                                    key: ddd,
                                    text: ddd,
                                    value: ddd
                                  }));
                                })()
                              : []
                          }
                          onChange={(e, { value }) => atualizarLinhaAditivo(idx, 'ddd', value as string)}
                          disabled={!clienteCompleto?.estado}
                          error={!linha.ddd && linha.planoSelecionado ? true : undefined}
                          style={{
                            borderRadius: '8px',
                            border: !linha.ddd && linha.planoSelecionado ? '2px solid #ff6b6b' : '2px solid #e0e0e0',
                            width: '100%'
                          }}
                        />
                        {!clienteCompleto?.estado && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Estado do cliente não disponível
                          </div>
                        )}
                        {!linha.ddd && linha.planoSelecionado && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            DDD é obrigatório
                          </div>
                        )}
                        {linha.fastchip && availableICcIds.length === 0 && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            ⚠️ Nenhum ICCID disponível para {
                              resolveRjMgEsFastchipIccidSearch(clienteCompleto?.estado)
                                ? formatRjMgEsFastchipClusterUiLabel(clienteCompleto?.estado)
                                : clienteCompleto?.estado
                            }
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Checkbox
                          label="Fastchip"
                          checked={linha.fastchip}
                          onChange={(e, { checked }) => {
                            const novoFastchip = checked || false;
                            
                            // Sincronizar fastchip em todas as linhas aditivas
                            // Obter ICCIDs já utilizados
                            const iccidsUtilizados = new Set<string>();
                            
                            const novasLinhas = linhasAditivo.map((la, index) => {
                              const dddsValidos = getValidDddsForFastchip(
                                clienteCompleto?.estado || '', 
                                novoFastchip, 
                                availableICcIds.length > 0
                              );
                              
                              // Se está ativando fastchip e há ICCIDs disponíveis, preencher
                              if (novoFastchip && availableICcIds.length > 0) {
                                // Se já tem ICCID e não está na lista de utilizados, manter
                                if (la.iccid && !iccidsUtilizados.has(la.iccid)) {
                                  iccidsUtilizados.add(la.iccid);
                                  return {
                                    ...la,
                                    fastchip: novoFastchip,
                                    iccid: la.iccid,
                                    ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                                  };
                                }
                                
                                // Se não tem ICCID, encontrar um disponível que não esteja sendo usado
                                const iccidDisponivel = availableICcIds.find(iccid => !iccidsUtilizados.has(iccid));
                                if (iccidDisponivel) {
                                  iccidsUtilizados.add(iccidDisponivel);
                                  return {
                                    ...la,
                                    fastchip: novoFastchip,
                                    iccid: iccidDisponivel,
                                    ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                                  };
                                }
                              }
                              
                              // Se está desativando, limpar ICCID
                              if (!novoFastchip) {
                                return {
                                  ...la,
                                  fastchip: novoFastchip,
                                  iccid: '',
                                  ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                                };
                              }
                              
                              // Manter estado atual mas atualizar fastchip
                              return {
                                ...la,
                                fastchip: novoFastchip,
                                ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                              };
                            });
                            
                            setLinhasAditivo(novasLinhas);
                          }}
                        />
                      </Table.Cell>
                      
                      <Table.Cell>
                        {linha.fastchip ? (
                          linha.iccid ? (
                            // Mostrar ICCID como texto readonly quando preenchido
                            <Input
                              value={linha.iccid}
                              readOnly
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e0e0e0',
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed'
                              }}
                            />
                          ) : (
                            // Se não tem ICCID ainda, mostrar dropdown para seleção manual (fallback)
                            <Dropdown
                              placeholder="Selecione ICCID"
                              selection
                              search
                              options={availableICcIds
                                .filter(iccid => !linhasAditivo.some(la => la.fastchip && la.iccid === iccid && la !== linha))
                                .map(iccid => ({
                                  key: iccid,
                                  text: iccid,
                                  value: iccid
                                }))}
                              value={linha.iccid || undefined}
                              onChange={(e, { value }) => atualizarLinhaAditivo(idx, 'iccid', value as string)}
                              loading={iccidsLoading}
                              disabled={!clienteCompleto || availableICcIds.length === 0}
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e0e0e0',
                              }}
                            />
                          )
                        ) : (
                          <div style={{ color: '#666', fontStyle: 'italic' }}>
                            Não necessário (chip da operadora)
                          </div>
                        )}
                        {iccidsError && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Erro ao carregar ICCIDs: {iccidsError}
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Dropdown
                          placeholder="Selecione o plano"
                          selection
                          search
                          options={planosOptionsAditivo}
                          value={linha.planoSelecionado || undefined}
                          onChange={(e, { value }) => atualizarLinhaAditivo(idx, 'planoSelecionado', value ? value as string : null)}
                          style={{
                            borderRadius: '8px',
                          }}
                          disabled={planosOptionsAditivo.length === 0}
                        />
                        {planosOptionsAditivo.length === 0 && !loading && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Carregando planos...
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}>
                          <Button
                            size="small"
                            positive={linha.seraUsadaPeloCliente === true}
                            onClick={() => atualizarLinhaAditivo(idx, 'seraUsadaPeloCliente', true)}
                            style={{
                              minWidth: '60px',
                              backgroundColor: linha.seraUsadaPeloCliente === true ? '#21ba45' : '#f8f9fa',
                              color: linha.seraUsadaPeloCliente === true ? '#ffffff' : '#495057'
                            }}
                          >
                            Sim
                          </Button>
                          <Button
                            size="small"
                            negative={linha.seraUsadaPeloCliente === false}
                            onClick={() => atualizarLinhaAditivo(idx, 'seraUsadaPeloCliente', false)}
                            style={{
                              minWidth: '60px',
                              backgroundColor: linha.seraUsadaPeloCliente === false ? '#db2828' : '#f8f9fa',
                              color: linha.seraUsadaPeloCliente === false ? '#ffffff' : '#495057'
                            }}
                          >
                            Não
                          </Button>
                        </div>
                        {linha.seraUsadaPeloCliente === null && linha.planoSelecionado && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Selecione se o cliente usará
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          icon="trash"
                          color="red"
                          size="small"
                          onClick={() => removerLinhaAditivo(idx)}
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}

          {/* Seção 1: Linhas para Renegociação - Mostrar primeiro se houver linhas */}
          {linhasRenovacao.length > 0 && (
          <Segment style={{ 
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Header as="h3" style={{ color: '#2c3e50', margin: 0 }}>
                <Icon name="refresh" />
                Linhas para Renegociação
              </Header>
              <Button
                icon="refresh"
                content="Recarregar Renegociações"
                onClick={recalcularTodasOfertas}
                disabled={!cliente || linhasRenovacao.length === 0}
                title="Recalcular ofertas aleatórias para todas as linhas de renegociação (exceto suspensas)"
                style={{ 
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  border: '1px solid #dee2e6'
                }}
              />
            </div>
            
            {deveExibirBannerPoliticaSuspensao(suspensionPolicy) && (
              <SuspensionBlockBanner
                size="tiny"
                variant="info"
                message={suspensionPolicy?.policyReferenceDateDescription ?? undefined}
              />
            )}

            <Table celled style={{ backgroundColor: '#ffffff' }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={1} style={{ textAlign: 'center' }}>Suspender</Table.HeaderCell>
                  <Table.HeaderCell>Número</Table.HeaderCell>
                  <Table.HeaderCell>Plano Atual</Table.HeaderCell>
                  <Table.HeaderCell width={1} style={{ textAlign: 'center' }}>WhatsApp</Table.HeaderCell>
                  <Table.HeaderCell>Plano Simulado</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {linhasRenovacao.map((lr, idx) => {
                  const planosDisponiveis = getPlanosDisponiveisParaLinha(lr.linha);
                  // Lógica invertida: se desmarcado = linha será suspensa
                  const estaMarcadaParaSuspender = linhasParaSuspender.has(lr.linha.id);
                  // Verificar status do WhatsApp
                  const temWhatsApp = lr.linha.temWhatsApp === true;
                  const semWhatsApp = lr.linha.temWhatsApp === false;
                  const whatsAppNaoValidado = lr.linha.temWhatsApp === null || lr.linha.temWhatsApp === undefined;
                  return (
                    <Table.Row key={lr.linha.id}>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <Checkbox
                            checked={estaMarcadaParaSuspender}
                            disabled={
                              (lr.linha as { suspensaoPermitida?: boolean | null }).suspensaoPermitida ===
                              false
                            }
                            onChange={(e, { checked }) => {
                              if (
                                (lr.linha as { suspensaoPermitida?: boolean | null }).suspensaoPermitida ===
                                false
                              ) {
                                return;
                              }
                              const novoSet = new Set(linhasParaSuspender);
                              // Se marcado (checked = true), adicionar à lista de suspensão
                              if (checked) {
                                novoSet.add(lr.linha.id);
                              } else {
                                // Se desmarcado (checked = false), remover da lista de suspensão
                                novoSet.delete(lr.linha.id);
                              }
                              setLinhasParaSuspender(novoSet);
                            }}
                            title={estaMarcadaParaSuspender ? "Desmarcar para não suspender" : "Marcar para suspender esta linha"}
                          />
                        </div>
                      </Table.Cell>
                      <Table.Cell>{lr.linha.numeroLinha}</Table.Cell>
                      <Table.Cell>{lr.linha.planoAtual}</Table.Cell>
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        {numerosEmValidacao.has(lr.linha.numeroLinha) ? (
                          <Icon 
                            loading 
                            name="spinner" 
                            color="blue" 
                            title="Validando WhatsApp..."
                            style={{ fontSize: '18px' }}
                          />
                        ) : (
                          <>
                            {temWhatsApp && (
                              <Icon 
                                name="check circle" 
                                color="green" 
                                title="Linha possui WhatsApp ativo"
                                style={{ fontSize: '18px' }}
                              />
                            )}
                            {semWhatsApp && (
                              <Icon 
                                name="times circle" 
                                color="red" 
                                title="Linha não possui WhatsApp ativo"
                                style={{ fontSize: '18px' }}
                              />
                            )}
                            {whatsAppNaoValidado && (
                              <Icon 
                                name="question circle" 
                                color="grey" 
                                title="WhatsApp ainda não foi validado"
                                style={{ fontSize: '18px' }}
                              />
                            )}
                          </>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Dropdown
                            placeholder="Selecione o plano"
                            selection
                            search
                            options={planosDisponiveis}
                            value={lr.planoSimulado || undefined}
                            onChange={(e, { value }) => atualizarLinhaRenovacao(idx, 'planoSimulado', value ? value as string : null)}
                            disabled={estaMarcadaParaSuspender || planosDisponiveis.length === 0}
                            style={{
                              borderRadius: '8px',
                              flex: 1
                            }}
                          />
                          {!estaMarcadaParaSuspender && (
                            <Button
                              icon="refresh"
                              size="mini"
                              circular
                              onClick={() => recalcularOfertaLinha(idx)}
                              title="Recalcular oferta inteligente para esta linha"
                              style={{ flexShrink: 0 }}
                            />
                          )}
                        </div>
                        {planosDisponiveis.length === 0 && !loading && !estaMarcadaParaSuspender && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Nenhum plano disponível para esta linha
                          </div>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Segment>
          )}

          {/* NOVA SEÇÃO: Adicionar Linhas Manualmente para Renegociação */}
          {cliente && (
            <Segment style={{ 
              marginTop: '20px', 
              marginBottom: '20px',
              background: 'rgba(255,255,255,0.05)', 
              border: '1px solid rgba(255,255,255,0.1)',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <Header as="h3" style={{ color: '#2c3e50', marginBottom: '15px' }}>
                <Icon name="plus circle" />
                Adicionar Linhas Manualmente para Renegociação
              </Header>
              
              <Form>
                <Form.Group widths="equal">
                  <Form.Field>
                    <label style={{ color: '#2c3e50' }}>Número da Linha</label>
                    <Input
                      placeholder="Ex: 11987654321"
                      id="manual-line-number"
                      style={{
                        borderRadius: '8px'
                      }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label style={{ color: '#2c3e50' }}>Franquia Atual</label>
                    <Input
                      placeholder="Ex: 20GB"
                      id="manual-line-data"
                      style={{
                        borderRadius: '8px'
                      }}
                    />
                  </Form.Field>
                  <Form.Field>
                    <label style={{ color: '#2c3e50' }}>Plano Atual (Opcional)</label>
                    <Dropdown
                      placeholder="Selecione o plano"
                      fluid
                      selection
                      search
                      options={planosOptions}
                      id="manual-line-plan"
                      clearable
                      style={{
                        borderRadius: '8px'
                      }}
                    />
                  </Form.Field>
                </Form.Group>
                <Button
                  primary
                  icon="plus"
                  content="Adicionar Linha para Renegociação"
                  onClick={adicionarLinhaManual}
                  disabled={validandoWhatsApp}
                  loading={validandoWhatsApp}
                  style={{
                    borderRadius: '8px'
                  }}
                />
              </Form>
            </Segment>
          )}

          {/* Seção: Novas Linhas (Aditivo) - Mostrar depois de Renegociação se houver linhas de renegociação */}
          {linhasRenovacao.length > 0 && linhasAditivo.length > 0 && (
            <Segment 
              ref={aditivosSectionRef}
              style={{ 
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #ff9800'
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <Header as="h3" style={{ color: '#e65100', margin: 0 }}>
                  <Icon name="plus circle" size="large" />
                  Novas Linhas (Aditivo)
                </Header>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    icon="refresh"
                    content="Recarregar Aditivos"
                    onClick={recalcularTodasOfertasAditivo}
                    disabled={!cliente || linhasAditivo.length === 0}
                    title="Recalcular planos aleatórios para todas as linhas aditivas"
                    style={{ 
                      backgroundColor: '#f8f9fa',
                      color: '#495057',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontWeight: 500
                    }}
                  />
                  <Button 
                    icon="plus" 
                    content="Adicionar Nova Linha" 
                    onClick={adicionarNovaLinha}
                    positive
                    style={{ 
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontWeight: 500
                    }}
                  />
                </div>
              </div>
              
              <Table celled style={{ backgroundColor: '#ffffff' }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>DDD</Table.HeaderCell>
                    <Table.HeaderCell>Fastchip</Table.HeaderCell>
                    <Table.HeaderCell>ICCID</Table.HeaderCell>
                    <Table.HeaderCell>Plano</Table.HeaderCell>
                    <Table.HeaderCell width={2} style={{ textAlign: 'center' }}>Cliente Usará</Table.HeaderCell>
                    <Table.HeaderCell width={1}>Ações</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {linhasAditivo.map((linha, idx) => (
                    <Table.Row key={idx}>
                      <Table.Cell>
                        <Dropdown
                          placeholder="Selecione DDD *"
                          selection
                          search
                          value={linha.ddd}
                          options={
                            clienteCompleto?.estado
                              ? (() => {
                                  const ddds = getValidDddsForFastchip(
                                    clienteCompleto.estado, 
                                    linha.fastchip, 
                                    availableICcIds.length > 0
                                  );
                                  return ddds.map(ddd => ({
                                    key: ddd,
                                    text: ddd,
                                    value: ddd
                                  }));
                                })()
                              : []
                          }
                          onChange={(e, { value }) => atualizarLinhaAditivo(idx, 'ddd', value as string)}
                          disabled={!clienteCompleto?.estado}
                          error={!linha.ddd && linha.planoSelecionado ? true : undefined}
                          style={{
                            borderRadius: '8px',
                            border: !linha.ddd && linha.planoSelecionado ? '2px solid #ff6b6b' : '2px solid #e0e0e0',
                            width: '100%'
                          }}
                        />
                        {!clienteCompleto?.estado && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Estado do cliente não disponível
                          </div>
                        )}
                        {!linha.ddd && linha.planoSelecionado && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            DDD é obrigatório
                          </div>
                        )}
                        {linha.fastchip && availableICcIds.length === 0 && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            ⚠️ Nenhum ICCID disponível para {
                              resolveRjMgEsFastchipIccidSearch(clienteCompleto?.estado)
                                ? formatRjMgEsFastchipClusterUiLabel(clienteCompleto?.estado)
                                : clienteCompleto?.estado
                            }
                          </div>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Checkbox
                          label="Fastchip"
                          checked={linha.fastchip}
                          onChange={(e, { checked }) => {
                            const novoFastchip = checked || false;
                            
                            // Sincronizar fastchip em todas as linhas aditivas
                            // Obter ICCIDs já utilizados
                            const iccidsUtilizados = new Set<string>();
                            
                            const novasLinhas = linhasAditivo.map((la, index) => {
                              const dddsValidos = getValidDddsForFastchip(
                                clienteCompleto?.estado || '', 
                                novoFastchip, 
                                availableICcIds.length > 0
                              );
                              
                              // Se está ativando fastchip e há ICCIDs disponíveis, preencher
                              if (novoFastchip && availableICcIds.length > 0) {
                                // Se já tem ICCID e não está na lista de utilizados, manter
                                if (la.iccid && !iccidsUtilizados.has(la.iccid)) {
                                  iccidsUtilizados.add(la.iccid);
                                  return {
                                    ...la,
                                    fastchip: novoFastchip,
                                    iccid: la.iccid,
                                    ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                                  };
                                }
                                
                                // Se não tem ICCID, encontrar um disponível que não esteja sendo usado
                                const iccidDisponivel = availableICcIds.find(iccid => !iccidsUtilizados.has(iccid));
                                if (iccidDisponivel) {
                                  iccidsUtilizados.add(iccidDisponivel);
                                  return {
                                    ...la,
                                    fastchip: novoFastchip,
                                    iccid: iccidDisponivel,
                                    ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                                  };
                                }
                              }
                              
                              // Se está desativando, limpar ICCID
                              if (!novoFastchip) {
                                return {
                                  ...la,
                                  fastchip: novoFastchip,
                                  iccid: '',
                                  ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                                };
                              }
                              
                              // Manter estado atual mas atualizar fastchip
                              return {
                                ...la,
                                fastchip: novoFastchip,
                                ddd: la.ddd && !dddsValidos.includes(la.ddd) ? '' : la.ddd
                              };
                            });
                            
                            setLinhasAditivo(novasLinhas);
                          }}
                        />
                      </Table.Cell>
                      
                      <Table.Cell>
                        {linha.fastchip ? (
                          linha.iccid ? (
                            // Mostrar ICCID como texto readonly quando preenchido
                            <Input
                              value={linha.iccid}
                              readOnly
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e0e0e0',
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed',
                                width: '100%'
                              }}
                            />
                          ) : (
                            // Se não tem ICCID ainda, mostrar dropdown para seleção manual (fallback)
                            <Dropdown
                              placeholder="Selecione ICCID"
                              selection
                              search
                              options={availableICcIds
                                .filter(iccid => !linhasAditivo.some(la => la.fastchip && la.iccid === iccid && la !== linha))
                                .map(iccid => ({
                                  key: iccid,
                                  text: iccid,
                                  value: iccid
                                }))}
                              value={linha.iccid || undefined}
                              onChange={(e, { value }) => atualizarLinhaAditivo(idx, 'iccid', value as string)}
                              disabled={iccidsLoading || availableICcIds.length === 0}
                              style={{
                                borderRadius: '8px',
                                border: '2px solid #e0e0e0',
                                width: '100%'
                              }}
                            />
                          )
                        ) : (
                          <div style={{ fontSize: '12px', color: '#6c757d' }}>
                            Não necessário
                          </div>
                        )}
                      </Table.Cell>
                      
                      <Table.Cell>
                        <Dropdown
                          placeholder="Selecione o plano"
                          selection
                          search
                          options={planosOptionsAditivo}
                          value={linha.planoSelecionado || undefined}
                          onChange={(e, { value }) => atualizarLinhaAditivo(idx, 'planoSelecionado', value ? value as string : null)}
                          style={{
                            borderRadius: '8px',
                            border: '2px solid #e0e0e0',
                            width: '100%'
                          }}
                        />
                      </Table.Cell>
                      
                      <Table.Cell style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}>
                          <Button
                            size="small"
                            positive={linha.seraUsadaPeloCliente === true}
                            onClick={() => atualizarLinhaAditivo(idx, 'seraUsadaPeloCliente', true)}
                            style={{
                              minWidth: '60px',
                              backgroundColor: linha.seraUsadaPeloCliente === true ? '#21ba45' : '#f8f9fa',
                              color: linha.seraUsadaPeloCliente === true ? '#ffffff' : '#495057'
                            }}
                          >
                            Sim
                          </Button>
                          <Button
                            size="small"
                            negative={linha.seraUsadaPeloCliente === false}
                            onClick={() => atualizarLinhaAditivo(idx, 'seraUsadaPeloCliente', false)}
                            style={{
                              minWidth: '60px',
                              backgroundColor: linha.seraUsadaPeloCliente === false ? '#db2828' : '#f8f9fa',
                              color: linha.seraUsadaPeloCliente === false ? '#ffffff' : '#495057'
                            }}
                          >
                            Não
                          </Button>
                        </div>
                        {linha.seraUsadaPeloCliente === null && linha.planoSelecionado && (
                          <div style={{ fontSize: '11px', color: '#ff6b6b', marginTop: '4px' }}>
                            Selecione se o cliente usará
                          </div>
                        )}
                      </Table.Cell>
                      
                      <Table.Cell>
                        <Button
                          icon="trash"
                          color="red"
                          size="mini"
                          onClick={() => removerLinhaAditivo(idx)}
                          title="Remover linha aditiva"
                        />
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}

          {/* Seção: Linhas para Suspender */}
          {linhasParaSuspender.size > 0 && (() => {
            const linhasSuspensas = linhasParaSuspender.size;
            const showAditivosRule = teamContractRules.requireAditivosForSuspendedLines;
            const aditivosNecessarios = showAditivosRule
              ? calcularAditivosNecessarios(linhasSuspensas)
              : 0;
            const linhasAditivoValidas = linhasAditivo.filter(la =>
              la.ddd && la.planoSelecionado && (!la.fastchip || la.iccid)
            ).length;
            const aditivosFaltando = showAditivosRule
              ? Math.max(0, aditivosNecessarios - linhasAditivoValidas)
              : 0;
            
            return (
              <Segment style={{ 
                backgroundColor: '#fff3e0',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: '4px solid #ff9800'
              }}>
                <Header as="h3" style={{ color: '#e65100', marginTop: 0 }}>
                  <Icon name="pause circle" />
                  Linhas para Suspender
                </Header>
                <Message warning style={{ marginBottom: '16px', backgroundColor: '#fff3e0', border: 'none' }}>
                  <Message.Header>Atenção</Message.Header>
                  <p>As linhas abaixo serão marcadas para suspensão. Elas não aparecerão no contrato nem no PDF.</p>
                </Message>
                
                {/* Indicador de regra de aditivos */}
                {showAditivosRule && (
                <Message 
                  info 
                  style={{ 
                    marginBottom: '16px', 
                    backgroundColor: aditivosFaltando > 0 ? '#ffebee' : '#e3f2fd', 
                    border: `2px solid ${aditivosFaltando > 0 ? '#f44336' : '#2196f3'}`,
                    borderRadius: '8px'
                  }}
                >
                  <Message.Header>
                    <Icon name={aditivosFaltando > 0 ? 'exclamation triangle' : 'info circle'} />
                    Regra de Aditivos
                  </Message.Header>
                  <p style={{ margin: '8px 0', fontWeight: 'bold' }}>
                    {linhasSuspensas} {linhasSuspensas === 1 ? 'linha suspensa requer' : 'linhas suspensas requerem'} {aditivosNecessarios} {aditivosNecessarios === 1 ? 'aditivo' : 'aditivos'}
                  </p>
                  {aditivosFaltando > 0 ? (
                    <p style={{ margin: '8px 0', color: '#c62828', fontWeight: 'bold' }}>
                      ⚠️ Faltam {aditivosFaltando} {aditivosFaltando === 1 ? 'aditivo' : 'aditivos'}. {aditivosFaltando === 1 ? 'Adicione' : 'Adicione'} mais {aditivosFaltando === 1 ? 'linha aditiva' : 'linhas aditivas'} antes de criar o contrato.
                    </p>
                  ) : (
                    <p style={{ margin: '8px 0', color: '#2e7d32', fontWeight: 'bold' }}>
                      ✅ Regra atendida: {linhasAditivoValidas} {linhasAditivoValidas === 1 ? 'aditivo encontrado' : 'aditivos encontrados'}
                    </p>
                  )}
                </Message>
                )}
              
              <List bulleted style={{ marginBottom: '20px' }}>
                {/* Usar apenas linhasRenovacao para evitar duplicatas */}
                {linhasRenovacao
                  .filter(lr => linhasParaSuspender.has(lr.linha.id))
                  .map(lr => (
                    <List.Item key={lr.linha.id}>
                      {lr.linha.numeroLinha} - {lr.linha.planoAtual}
                    </List.Item>
                  ))}
              </List>
              </Segment>
            );
          })()}

          {/* Seção 4: Resultado da Simulação */}
          {resultadoSimulacao && (
            <Segment 
              ref={resultadoSectionRef}
              style={{ 
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <Header as="h3" color="green" style={{ color: '#2e7d32', marginTop: 0 }}>
                <Icon name="chart line" />
                Resultado da Simulação
              </Header>
              
              <Message success style={{ backgroundColor: '#e8f5e9', border: 'none', borderRadius: '8px' }}>
                <Message.Header>Simulação Calculada</Message.Header>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: '16px' }}>
                  <div>
                    <strong>Total Renegociação:</strong><br />
                    <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                      R$ {resultadoSimulacao.valorTotalRenovacao.toFixed(2)}
                    </span><br />
                    <small>{resultadoSimulacao.totalLinhasRenovacao} linhas</small>
                  </div>
                  <div>
                    <strong>Total Aditivo:</strong><br />
                    <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                      R$ {resultadoSimulacao.valorTotalAditivo.toFixed(2)}
                    </span><br />
                    <small>{resultadoSimulacao.totalLinhasAditivo} linhas</small>
                  </div>
                  <div>
                    <strong>Valor Total:</strong><br />
                    <span style={{ 
                      fontSize: '20px', 
                      fontWeight: 'bold', 
                      color: cliente 
                        ? resultadoSimulacao.valorTotal < cliente.valorFatura 
                          ? '#2e7d32' // Verde (menor que fatura atual = economia)
                          : resultadoSimulacao.valorTotal > cliente.valorFatura 
                            ? '#c62828' // Vermelho (maior que fatura atual = aumento)
                            : '#1976d2' // Azul (igual = sem mudança)
                        : '#2e7d32'
                    }}>
                      R$ {resultadoSimulacao.valorTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Message>

              <Table celled style={{ backgroundColor: '#ffffff', marginTop: '16px' }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Número</Table.HeaderCell>
                    <Table.HeaderCell>Plano Anterior</Table.HeaderCell>
                    <Table.HeaderCell>Plano Novo</Table.HeaderCell>
                    {/* <Table.HeaderCell>Valor Anterior</Table.HeaderCell> */}
                    <Table.HeaderCell>Valor Novo</Table.HeaderCell>
                    <Table.HeaderCell>Diferença</Table.HeaderCell>
                    <Table.HeaderCell>Tipo</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {resultadoSimulacao.detalhesLinhas.map((detalhe, idx) => (
                    <Table.Row 
                      key={idx}
                      style={{
                        backgroundColor: detalhe.seraSuspensa ? '#ffebee' : 'transparent'
                      }}
                    >
                      <Table.Cell 
                        style={{
                          color: detalhe.seraSuspensa ? '#c62828' : 'inherit',
                          textDecoration: detalhe.seraSuspensa ? 'line-through' : 'none',
                          fontStyle: detalhe.seraSuspensa ? 'italic' : 'normal'
                        }}
                      >
                        {detalhe.numeroLinha}
                        {detalhe.seraSuspensa && (
                          <Label color="red" size="mini" style={{ marginLeft: '8px' }}>
                            SUSPENSA
                          </Label>
                        )}
                      </Table.Cell>
                      <Table.Cell
                        style={{
                          color: detalhe.seraSuspensa ? '#c62828' : 'inherit',
                          textDecoration: detalhe.seraSuspensa ? 'line-through' : 'none'
                        }}
                      >
                        {detalhe.planoAnterior}
                      </Table.Cell>
                      <Table.Cell
                        style={{
                          color: detalhe.seraSuspensa ? '#c62828' : 'inherit',
                          textDecoration: detalhe.seraSuspensa ? 'line-through' : 'none'
                        }}
                      >
                        {detalhe.planoNovo}
                      </Table.Cell>
                      {/* <Table.Cell>
                        {detalhe.valorAnterior > 0 
                          ? `R$ ${detalhe.valorAnterior.toFixed(2)}` 
                          : '-'}
                      </Table.Cell> */}
                      <Table.Cell
                        style={{
                          color: detalhe.seraSuspensa ? '#c62828' : 'inherit',
                          textDecoration: detalhe.seraSuspensa ? 'line-through' : 'none'
                        }}
                      >
                        R$ {detalhe.valorNovo.toFixed(2)}
                      </Table.Cell>
                      <Table.Cell style={{ 
                        color: detalhe.seraSuspensa 
                          ? '#c62828' 
                          : detalhe.diferenca < 0 
                            ? '#2e7d32' // Verde para economia (negativo)
                            : detalhe.diferenca > 0 
                              ? '#c62828' // Vermelho para aumento (positivo)
                              : '#1976d2', // Azul para sem mudança (zero)
                        fontWeight: 'bold',
                        textDecoration: detalhe.seraSuspensa ? 'line-through' : 'none'
                      }}>
                        {detalhe.seraSuspensa ? 'R$ 0,00' : `R$ ${detalhe.diferenca.toFixed(2)}`}
                      </Table.Cell>
                      <Table.Cell
                        style={{
                          color: detalhe.seraSuspensa ? '#c62828' : 'inherit',
                          textDecoration: detalhe.seraSuspensa ? 'line-through' : 'none'
                        }}
                      >
                        {detalhe.tipoOperacao}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}
                </Tab.Pane>
              )
            },
            {
              menuItem: { 
                key: 'arguments', 
                icon: 'comments', 
                content: 'Argumentação' 
              },
              render: () => (
                <Tab.Pane style={{ padding: 0 }}>
                  <SalesArgumentsPanel />
                </Tab.Pane>
              )
            }
          ]}
          activeIndex={activeTab}
          onTabChange={(e, { activeIndex }) => setActiveTab(activeIndex as number)}
        />
      </Modal.Content>
        
        <Modal.Actions style={{ 
          padding: '16px 24px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Valor Simulado */}
          {cliente && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginRight: 'auto'
            }}>
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px' }}>
                Valor Simulado
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4caf50' }}>
                R$ {resultadoSimulacao ? resultadoSimulacao.valorTotal.toFixed(2) : calcularValorSimulado().toFixed(2)}
              </div>
              {(resultadoSimulacao ? resultadoSimulacao.valorTotal : calcularValorSimulado()) > 0 && cliente && (() => {
                const diferencaTotal = (resultadoSimulacao ? resultadoSimulacao.valorTotal : calcularValorSimulado()) - cliente.valorFatura;
                return (
                  <div style={{ 
                    fontSize: '11px', 
                    color: diferencaTotal < 0 
                      ? '#2e7d32' // Verde (economia)
                      : diferencaTotal > 0 
                        ? '#c62828' // Vermelho (aumento)
                        : '#1976d2', // Azul (sem mudança)
                    marginTop: '2px',
                    fontWeight: '500'
                  }}>
                    Diferença: R$ {diferencaTotal.toFixed(2)}
                  </div>
                );
              })()}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button 
              onClick={handleClose} 
              disabled={calculando}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: 500,
              }}
            >
              Cancelar
            </Button>
            <Button 
              primary 
              onClick={handleCalculate}
              loading={calculando}
              disabled={loading}
              style={{
                borderRadius: '8px',
                padding: '10px 24px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Icon name="check" />
              Confirmar Simulação
            </Button>
          </div>
          {canShowFastContractButton() && (
            <Button 
              color="green"
              onClick={handleFastContract}
              disabled={loading || calculando}
              style={{
                borderRadius: '8px',
                padding: '10px 24px',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                color: 'white',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.4)';
              }}
              onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <Icon name="lightning" />
              Contrato Rápido
            </Button>
          )}
        </Modal.Actions>
      </Modal>
    );
  };

export default SimulationModal;
